import React, { FC, useMemo, useState, useCallback } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { prepare, trackClick as baseTrackClick } from 'utils/analyticsV2'

import ClockIcon from 'svgs/access-time-filled'

import CoreText from 'components/Core/CoreText'

import Calendar, { CalendarMarks, setMark } from './Calendar'
import Period, { PERIODS } from './Period'
import { Block } from './Time'

import styles from './styles.module.scss'

export type { Block } from './Time'

dayjs.extend(utc)
dayjs.extend(timezone)

const trackClick = prepare(
  {
    family: 'vendor-site-visit',
    screen: 'unscheduled', // This will have to change if we reuse this component!
    section: 'availability',
  },
  baseTrackClick
)

interface Props {
  availabilities: Array<Block>
  selected?: Block
  eventDuration: number
  disabled: boolean
  timezone: string
  onChange: (block: Block) => void
}
const Availabilities: FC<Props> = ({ availabilities, selected, eventDuration, disabled, timezone, onChange }) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (availabilities.length > 0) {
      return availabilities[0].start
    }
    return new Date()
  })

  const djsSelectedDate = useMemo(() => {
    return dayjs(selectedDate).tz(timezone)
  }, [selectedDate, timezone])

  const handleTrackAttempted = useCallback((code: string, flexfield_1: any) => {
    trackClick({ code, flexfield_1 })
  }, [])

  const firstAvailability = useMemo(() => {
    if (availabilities.length > 0) {
      return availabilities[0].start
    }
  }, [availabilities])

  const todaysAvailabilities = useMemo(() => {
    return availabilities.filter((block) => dayjs(djsSelectedDate).isSame(dayjs(block.start).tz(timezone), 'day'))
  }, [djsSelectedDate, availabilities, timezone])

  const marks = useMemo(() => {
    return availabilities.reduce((marks, block) => {
      return setMark(marks, dayjs(block.start).tz(timezone), 'default')
    }, {} as CalendarMarks)
  }, [availabilities, timezone])

  return (
    <div className={styles.availabilities}>
      <Calendar
        className={styles.calendar}
        selected={selectedDate}
        onSelectionChange={setSelectedDate}
        marks={marks}
        timezone={timezone}
        onTrackAttempt={handleTrackAttempted}
      />
      <div className="tw:w-full tw:flex tw:items-center tw:justify-between">
        <div className={styles.selectedDate}>
          <div className={styles.mainLine}>
            <CoreText.Paragraph>{djsSelectedDate.format('ddd, MMM D')}</CoreText.Paragraph>
            {firstAvailability && dayjs(firstAvailability).tz(timezone).isSame(djsSelectedDate, 'day') && (
              <div className="tw:p-1 tw:flex tw:bg-green-700 tw:text-white tw:rounded-lg tw:text-sm tw:font-semibold tw:-my-1 tw:leading-tight">
                {'1st Availability'}
              </div>
            )}
          </div>
          <CoreText.Paragraph
            className={cx({ [styles.notPacific]: timezone != 'America/Los_Angeles' })}
            weight="light"
            color="tertiaryBlack"
          >
            {djsSelectedDate.format('zzz')}
          </CoreText.Paragraph>
        </div>
        <div className="tw:my-2 tw:mx-4 tw:p-2 tw:flex tw:bg-blue-100 tw:rounded-lg tw:gap-1">
          <ClockIcon className="tw:!size-6 tw:text-blue-700" />
          <div className="tw:text-primary-black tw:text-sm tw:font-semibold">{`${eventDuration} mins`}</div>
        </div>
      </div>
      {todaysAvailabilities.length == 0 ? (
        <CoreText.Paragraph
          className={styles.noTimes}
          color="disabledBlack"
          weight="light"
        >{`No available times for the selected date`}</CoreText.Paragraph>
      ) : (
        <ol>
          {PERIODS.map((period) => (
            <Period
              key={period}
              period={period}
              availabilities={todaysAvailabilities}
              selected={selected}
              disabled={disabled}
              timezone={timezone}
              onChange={onChange}
            />
          ))}
        </ol>
      )}
    </div>
  )
}

export default Availabilities
